// pages/SubscriptionPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import CategoryExplorer from "../components/subscription/CategoryExplorer";
import PlanGrid from "../components/subscription/PlanGrid";
import SubscriptionStatus from "../components/subscription/SubscriptionStatus";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../utils/httpRequest";
import { toast } from "react-toastify";

const { httpRequest: _maybeGlobal } = window || {};

const SubscriptionPage = () => {
  const { t, i18n } = useTranslation();

  // اگر user را از کانتکست دارید از همان استفاده کنید
  const user = JSON.parse(localStorage.getItem("user"));

  // مسیر ناوبری دسته‌ها
  const [path, setPath] = useState([]); // [{_id, title, level}]
  // همهٔ دسته‌ها (از بک‌اند)
  const [allCategories, setAllCategories] = useState([]);
  // دسته‌های نمایش‌داده‌شده در لِوِل فعلی
  const [currentCategories, setCurrentCategories] = useState([]);
  // پلن‌های قابل نمایش برای کتگوری لول ۳
  const [selectedPlans, setSelectedPlans] = useState([]);

  // اشتراک‌های کاربر
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);

  const getCurrentPlanForCategory = (categoryId) => {
    const activeSubscription = subscriptions.find(
      (s) =>
        String(s.categoryId) === String(categoryId) && s.status === "active",
    );

    return activeSubscription?.plan || null;
  };

  // ------- پلن‌های ثابت برای همهٔ لول ۳ -------
  const buildPlansForLevel3 = (categoryTitle) => [
    {
      key: "free",
      title: `عضویت رایگان – ${categoryTitle}`,
      price: "رایگان",
      features: [
        "زمان: ۱ هفته",
        "تعداد آزمون: ۱ آزمون",
        "امکانات: پاسخنامه سیستمی",
        "هوش مصنوعی/راهنما: ندارد",
      ],
      buttonText: "فعالسازی رایگان",
    },
    {
      key: "basic",
      title: `عضویت پایه – ${categoryTitle}`,
      price: "۲۰۰٬۰۰۰",
      features: [
        "زمان: ۲ ماه",
        "تعداد آزمون: نامحدود",
        "امکانات: پاسخنامه سیستمی",
        "هوش مصنوعی/راهنما: ندارد",
      ],
      buttonText: "خرید پایه",
    },
    {
      key: "pro",
      title: `عضویت پیشرفته – ${categoryTitle}`,
      price: "۵۰۰٬۰۰۰",
      features: [
        "زمان: ۲ ماه",
        "تعداد آزمون: نامحدود",
        "امکانات: پاسخنامه تشریحی",
        "هوش مصنوعی/راهنما: دارد",
      ],
      buttonText: "خرید پیشرفته",
    },
  ];
  // --------------------------------------------

  // لود دسته‌ها از بک‌اند
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const resp = await httpRequest(
          "GET",
          "/category",
          {},
          { "x-access-token": user?.token },
        );
        const cats = resp?.data?.data || resp?.data || [];
        if (abort) return;
        setAllCategories(cats);

        // ریشه‌ها (level=1 یا parent=null)
        const roots = cats.filter(
          (c) => c?.level === 1 || c?.parent == null || c?.parent === "null",
        );
        setCurrentCategories(roots);
      } catch (e) {
        console.error("Category fetch error:", e);
        setAllCategories([]);
        setCurrentCategories([]);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  // لود اشتراک‌های کاربر
  useEffect(() => {
    if (!user) return;
    let abort = false;
    (async () => {
      setSubsLoading(true);
      setSubsError(null);
      try {
        const resp = await httpRequest(
          "GET",
          `/subscription/${user?._id}`,
          {},
          { "x-access-token": user?.token },
        );
        const serverSubs = resp?.data?.data || resp?.data || [];
        if (abort) return;

        const mapped = (serverSubs || []).map((s) => {
          const categoryTitle =
            s?.category?.title || s?.categoryTitle || s?.categoryName || "—";
          const endAt = s?.endAt ? new Date(s.endAt) : null;
          const status =
            typeof s?.isActive === "boolean"
              ? s.isActive
                ? "active"
                : "expired"
              : endAt && endAt > new Date()
                ? "active"
                : "expired";
          return {
            _id: s._id,
            categoryId: s?.category?._id || s?.category,
            categoryTitle,
            status,
            endDate: s?.endAt,
            purchaseDate: s?.startAt,
            plan: s?.plan,
            autoRenew: s?.autoRenew,
            remainingDays: s?.remainingDays,
            usage: s?.usage,
          };
        });

        const activeSub = mapped.find((s) => s.status === "active");
        setCurrentPlan(activeSub?.plan || null);

        setSubscriptions(mapped);
      } catch (e) {
        setSubsError(e?.response?.data?.message || e.message || "Error");
      } finally {
        if (!abort) setSubsLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [user?._id]);

  const isRtl = i18n.language.includes("fa");
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  // انتخاب دسته
  const handleCategorySelect = (cat) => {
    const newPath = [
      ...path,
      { _id: cat._id, title: cat.title, level: cat.level },
    ];

    setPath(newPath);

    const children = allCategories.filter(
      (c) => String(c.parent) === String(cat._id),
    );

    if (cat.level === 3) {
      setSelectedPlans(
        buildPlansForLevel3(cat.title).map((plan) => ({
          ...plan,
          category: cat,
        })),
      );

      setCurrentCategories([]);
    } else if (children.length > 0) {
      setCurrentCategories(children);
      setSelectedPlans([]);
    } else {
      setCurrentCategories([]);
      setSelectedPlans([]);
    }
  };

  // کلیک روی مسیر (breadcrumb)
  const handlePathClick = (index) => {
    if (index === -1) {
      const roots = allCategories.filter(
        (c) => c?.level === 1 || c?.parent == null || c?.parent === "null",
      );
      setPath([]);
      setCurrentCategories(roots);
      setSelectedPlans([]);
      return;
    }

    const newPath = path.slice(0, index + 1);
    setPath(newPath);

    const last = newPath[newPath.length - 1];
    const children = allCategories.filter(
      (c) => String(c.parent) === String(last._id),
    );

    if (children.length > 0) {
      setCurrentCategories(children);
      setSelectedPlans([]);
    } else if (last.level === 3) {
      const category = allCategories.find(
        (c) => String(c._id) === String(last._id),
      );

      setCurrentCategories([]);

      setSelectedPlans(
        buildPlansForLevel3(last.title).map((plan) => ({
          ...plan,
          category,
        })),
      );
    } else {
      setCurrentCategories([]);
      setSelectedPlans([]);
    }
  };

  // بخش وضعیت اشتراک‌ها: لودینگ/خطا/نمایش
  const subsSection = useMemo(() => {
    if (subsLoading) {
      return (
        <div className="w-full mb-6">
          <div className="animate-pulse h-24 bg-white rounded-xl shadow-sm" />
        </div>
      );
    }
    if (subsError) {
      return (
        <div className="w-full mb-6">
          <div className="p-4 bg-red-50 text-red-700 rounded-xl shadow-sm">
            {t("common.error")}: {subsError}
          </div>
        </div>
      );
    }
    return (
      <SubscriptionStatus onRenew={onPlanClick} subscriptions={subscriptions} />
    );
  }, [subsLoading, subsError, subscriptions, t]);

  async function onPlanClick(plan) {
    try {
      const categoryId = plan?.categoryId || plan.category?._id;
      console.log(categoryId);
      if (!categoryId) {
        throw new Error("Category not found");
      }

      const activeSubscription = subscriptions.find(
        (s) =>
          String(s.categoryId) === String(categoryId) && s.status === "active",
      );

      const mode = activeSubscription ? "renew" : "buy";

      const { data } = await httpRequest(
        "POST",
        "/subscription/generate-payment-link",
        {
          categoryId: categoryId,
          plan: plan?.key || plan?.plan,
          mode,
        },
        {
          "x-access-token": user?.token,
        },
      );

      const paymentUrl =
        data?.paymentUrl || data?.data?.paymentUrl || data?.data?.url;

      if (!paymentUrl) {
        throw new Error("Payment url not received");
      }

      window.location.href = paymentUrl;
    } catch (err) {
      console.error(err);

      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "خطا در ایجاد لینک پرداخت",
      );
    }
  }

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("subscription_page.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          {subsSection}

          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            {t("subscription_page.select_subscription")}
          </h1>

          <CategoryExplorer
            path={path.map((p) => p.title)}
            categories={currentCategories}
            onSelect={handleCategorySelect}
            onPathClick={handlePathClick}
          />

          {selectedPlans.length > 0 && (
            <>
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                {t("subscription_page.available_subscriptions", {
                  category: path[path.length - 1]?.title,
                })}
              </h2>
              <PlanGrid
                plans={selectedPlans}
                currentPlan={getCurrentPlanForCategory(
                  path[path.length - 1]?._id,
                )}
                currentCategoryId={path[path.length - 1]?._id}
                onPlanClick={onPlanClick}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SubscriptionPage;
